import { EvidenceStatus, OAuthProvider, SyncStatus } from '@seapoint/server/enums';
import type { LucideIcon } from 'lucide-react';
import { CheckCircle2, CircleAlert, Clock, FileDownIcon, FileTextIcon } from 'lucide-react';
import { Button } from '../../../../../design-system/button/button';
import { Inline } from '../../../../../design-system/inline/inline';
import { Stack } from '../../../../../design-system/stack/stack';
import {
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '../../../../../design-system/tooltip/tooltip-primitives';
import { exhaustiveCheck } from '../../../../../utils/exhaustive-check';
import { OAuthProviderLogoIcon } from '../../../../invoice-pay/components/oauth-provider-logo-icon';

type XeroSyncConfig =
  | {
      status: Extract<SyncStatus, SyncStatus.FAILED>;
      error: string | null;
    }
  | {
      status: Exclude<SyncStatus, SyncStatus.FAILED>;
    }
  | null;

type EvidenceCardProps = {
  fileName: string;
  documentId: string;
  evidenceStatus?: Extract<EvidenceStatus, EvidenceStatus.COMPLETED>;
  xeroSyncConfig?: XeroSyncConfig;
  onViewEvidence: (documentId: string) => void;
  onDownloadEvidence: (documentId: string) => void;
};

export const EvidenceCard = ({
  fileName,
  documentId,
  evidenceStatus,
  xeroSyncConfig,
  onViewEvidence,
  onDownloadEvidence,
}: EvidenceCardProps) => {
  // Only show Xero status if evidence is COMPLETED (evidenceStatus is already typed as COMPLETED only)
  const statusDisplay = evidenceStatus ? getStatusDisplay(xeroSyncConfig ?? null) : null;
  const StatusIcon = statusDisplay?.icon;

  return (
    <Stack space={0} className="w-full overflow-hidden rounded-lg bg-accent">
      {/* Top row: File info and actions */}
      <Inline align="center" className="w-full px-5 py-3" justify="between">
        <Inline space={3} align="center">
          <FileTextIcon className="h-6 w-6" strokeWidth={1} />
          <button className="text-sm font-normal text-link hover:underline" onClick={() => onViewEvidence(documentId)}>
            {fileName}
          </button>
        </Inline>
        <Button onClick={() => onDownloadEvidence(documentId)} variant="ghost" aria-label="Download Evidence">
          <FileDownIcon className="h-6 w-6 text-muted-foreground" strokeWidth={1} />
        </Button>
      </Inline>

      {/* Bottom row: Xero sync status */}
      {statusDisplay && (
        <Inline space={2} className={`w-full px-5 py-3 ${statusDisplay.bgColor}`} align="center" justify="between">
          <Inline space={2} align="center">
            <OAuthProviderLogoIcon provider={OAuthProvider.XERO} className="h-4 w-4" />
            <span className={`text-xs ${statusDisplay.color}`}>{statusDisplay.text}</span>
          </Inline>
          {StatusIcon && (
            <div className="flex items-center justify-center px-4">
              {xeroSyncConfig && xeroSyncConfig.status === SyncStatus.FAILED && xeroSyncConfig.error ? (
                <TooltipProvider>
                  <TooltipRoot>
                    <TooltipTrigger asChild>
                      <StatusIcon className={`h-4 w-4 ${statusDisplay.iconColor} cursor-help`} />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs text-xs">{xeroSyncConfig.error}</p>
                    </TooltipContent>
                  </TooltipRoot>
                </TooltipProvider>
              ) : (
                <StatusIcon className={`h-4 w-4 ${statusDisplay.iconColor}`} />
              )}
            </div>
          )}
        </Inline>
      )}
    </Stack>
  );
};

type StatusDisplay = {
  text: string;
  color: string;
  icon: LucideIcon;
  iconColor: string;
  bgColor: string;
};

const getStatusDisplay = (xeroSyncConfig: XeroSyncConfig): StatusDisplay => {
  if (!xeroSyncConfig) {
    return {
      text: 'Xero synchronization pending',
      color: 'text-gray-600',
      icon: Clock,
      iconColor: 'text-gray-600',
      bgColor: 'bg-gray-50',
    };
  }

  const status = xeroSyncConfig.status;

  switch (status) {
    case SyncStatus.COMPLETE:
      return {
        text: 'Synced to Xero',
        color: 'text-green-600',
        icon: CheckCircle2,
        iconColor: 'text-green-600',
        bgColor: 'bg-green-50',
      };
    case SyncStatus.FAILED:
      return {
        text: 'Failed to sync to Xero',
        color: 'text-red-600',
        icon: CircleAlert,
        iconColor: 'text-red-600',
        bgColor: 'bg-red-50',
      };
    case SyncStatus.IN_PROGRESS:
    case SyncStatus.PENDING:
      return {
        text: 'Xero sync pending',
        color: 'text-gray-600',
        icon: Clock,
        iconColor: 'text-gray-600',
        bgColor: 'bg-gray-50',
      };
    default:
      return exhaustiveCheck(status);
  }
};
